// Step 3: the Excel-like review grid (RevoGrid).
// Inline edit, range select + Excel copy/paste + fill, column sort & filter, global find,
// per-cell error highlight + tooltip, an issues panel, per-row Product/Length/Location pickers,
// and add/delete rows. Confirmed rows are submitted to the backend import (all-or-nothing).

import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { RevoGrid, Editor } from '@revolist/react-datagrid';
import type { ColumnRegular, RowDefinition } from '@revolist/revogrid';
import type { TFunction } from 'i18next';
import { Template } from '~/pages/inventory-import/editors/template';
import { NumberEditor } from '~/pages/inventory-import/editors/NumberEditor';
import {
  DeleteCell,
  EnumCell,
  LocationCell,
  ProductCell,
} from '~/pages/inventory-import/editors/cells';
import { rewriteCopiedData } from '~/pages/inventory-import/lib/clipboardRewrite';
import { MAX_ROWS } from '~/pages/inventory-import/lib/parseFile';
import { SELLER_FIELDS } from '~/pages/inventory-import/lib/targetFields';
import { buildRows, type GridRow } from '~/pages/inventory-import/lib/rows';
import { validateRows } from '~/pages/inventory-import/lib/validate';
import { measureBody } from '~/pages/inventory-import/lib/textWidth';
import {
  fitColumnWidth,
  COL_PAD,
} from '~/pages/inventory-import/lib/columnWidths';
import { toImportRows } from '~/pages/inventory-import/lib/importPayload';
import { useImportSellerInventory } from '~/pages/inventory-import/api/useImportSellerInventory';
import { translateIssueMessage } from '~/pages/inventory-import/lib/fieldLabel';
import { FocusBanner } from '~/pages/inventory-import/steps/FocusBanner';
import { ImportActions } from '~/pages/inventory-import/steps/ImportActions';
import { IssuesPanel } from '~/pages/inventory-import/steps/IssuesPanel';
import { ReviewToolbar } from '~/pages/inventory-import/steps/ReviewToolbar';
import { ServerErrorList } from '~/pages/inventory-import/steps/ServerErrorList';
import { SummaryBar } from '~/pages/inventory-import/steps/SummaryBar';
import { useReviewRows } from '~/pages/inventory-import/steps/useReviewRows';
import {
  locationDisplay,
  productDisplay,
} from '~/pages/inventory-import/steps/rowDisplay';
import type {
  ColumnMapping,
  ParsedFile,
} from '~/pages/inventory-import/lib/types';

// RevoGrid cell highlights are inline styles (Tailwind can't reach inside the grid), but CSS
// custom properties inherit through, so reference the design tokens directly — matching the
// SummaryBar legend's bg-error-100 / bg-warning-100 classes, keeping the legend and grid in sync.
const ERROR_BG = 'var(--color-error-100)';
const WARN_BG = 'var(--color-warning-100)';

interface ReviewStepProps {
  file: ParsedFile;
  mapping: ColumnMapping;
  onBack: () => void;
  backLabel: string;
}

interface Handlers {
  onDelete: (id: number) => void;
  onPick: (id: number, productId: string) => void;
  onSet: (id: number, field: string, value: string) => void;
}
type HandlersRef = { current: Handlers };
type TRef = { current: TFunction };

function highlight(prop: string, tRef: TRef): ColumnRegular['cellProperties'] {
  return ({ model }) => {
    const issue = (model as GridRow).__issues?.[prop];
    if (!issue) return undefined;
    return {
      // native hover tooltip explaining what's wrong
      title: translateIssueMessage(
        tRef.current,
        issue.messageKey,
        issue.params
      ),
      style: { backgroundColor: issue.level === 'error' ? ERROR_BG : WARN_BG },
    };
  };
}

// RevoGrid is fixed-height per row, so to wrap long content we size each row to fit its content.
const LINE_PX = 20;
const ROW_PAD_PX = 16;
const MIN_ROW_PX = 44;
const MAX_ROW_LINES = 4;
// The grid grows to fit its content; cap the height so a very large import keeps an inner scroll.
const GRID_HEADER_PX = 45;
const GRID_MAX_PX = 2000;

type ColumnWidths = Record<string, number>;

// The content columns whose width is fitted to their data (and which can wrap, growing the row).
// Each is sized to the widest of its rendered values and its (2-line-wrapped) header, clamped to
// [min, max]; values past `max` wrap. `text` returns exactly what the cell renders, so the fitted
// width and the wrap estimate agree. Keyed by the RevoGrid column `prop`.
const FIT_COLUMNS: {
  prop: string;
  headerKey: string;
  min: number;
  max: number;
  text: (row: GridRow) => string;
}[] = [
  {
    prop: 'product',
    headerKey: 'fields.product',
    min: 160,
    max: 340,
    text: productDisplay,
  },
  {
    prop: 'length',
    headerKey: 'fields.length',
    min: 90,
    max: 180,
    text: (row) => String(row.length ?? ''),
  },
  {
    prop: 'location',
    headerKey: 'fields.location',
    min: 170,
    max: 340,
    text: locationDisplay,
  },
  ...SELLER_FIELDS.flatMap((f) =>
    ['descriptor', 'length', 'locationCode'].includes(f.key)
      ? []
      : [
          {
            prop: f.key,
            headerKey: `fields.${f.key}`,
            min: f.key === 'unit' ? 80 : 96,
            max: f.key === 'unit' ? 130 : 200,
            text: (row: GridRow) => String(row[f.key] ?? ''),
          },
        ]
  ),
];

// Fit every content column to the imported data once (the data is known up front). Editing a cell
// later doesn't re-fit; the user can still drag-resize. All content columns are sortable+filterable,
// so the header carries sort/filter icons.
function computeColumnWidths(rows: GridRow[], t: TFunction): ColumnWidths {
  const widths: ColumnWidths = {};
  for (const col of FIT_COLUMNS) {
    widths[col.prop] = fitColumnWidth({
      header: t(col.headerKey),
      values: rows.map(col.text),
      min: col.min,
      max: col.max,
      hasIcons: true,
    });
  }
  return widths;
}

function estimateRowHeight(row: GridRow, widths: ColumnWidths): number {
  let lines = 1;
  for (const col of FIT_COLUMNS) {
    const size = widths[col.prop] ?? col.min;
    const avail = Math.max(40, size - COL_PAD);
    const needed = Math.ceil(measureBody(col.text(row)) / avail) || 1;
    lines = Math.max(lines, Math.min(MAX_ROW_LINES, needed));
  }
  return Math.max(MIN_ROW_PX, lines * LINE_PX + ROW_PAD_PX);
}

function buildColumns(
  handlersRef: HandlersRef,
  tRef: TRef,
  widths: ColumnWidths
): ColumnRegular[] {
  const t = tRef.current;
  const actionsCol: ColumnRegular = {
    prop: '__actions',
    name: '',
    size: 48,
    pin: 'colPinStart',
    readonly: true,
    cellTemplate: Template(DeleteCell, { handlers: handlersRef }),
    // Remove the theme's horizontal cell padding and center the delete button across the full cell
    // (inline style overrides the compact `padding: 0 15px`, which was pushing the ✕ off-center).
    cellProperties: () => ({
      style: { padding: '0', justifyContent: 'center' },
    }),
  };
  // Resolved pickers. Not readonly, so Excel paste lands in the cell and is resolved on edit.
  const productCol: ColumnRegular = {
    prop: 'product',
    name: t('fields.product'),
    size: widths.product ?? 260,
    sortable: true,
    filter: true,
    cellTemplate: Template(ProductCell, { handlers: handlersRef }),
    cellProperties: highlight('product', tRef),
    cellParser: (model) => productDisplay(model as GridRow),
  };
  // Free-text length: the user can type any unit (8 ft, 96", 2438mm); on edit we parse the
  // value+unit to inches and validate against the resolved product's DB lengths. The cell shows
  // the raw entered text; the inches-based issue (keyed 'lengthInches') still highlights here.
  const lengthCol: ColumnRegular = {
    prop: 'length',
    name: t('fields.length'),
    size: widths.length ?? 140,
    sortable: true,
    filter: true,
    cellProperties: highlight('lengthInches', tRef),
    cellParser: (model) => String((model as GridRow).length ?? ''),
  };
  const locationCol: ColumnRegular = {
    prop: 'location',
    name: t('fields.location'),
    size: widths.location ?? 200,
    sortable: true,
    filter: true,
    cellTemplate: Template(LocationCell, { handlers: handlersRef }),
    cellProperties: highlight('location', tRef),
    cellParser: (model) => locationDisplay(model as GridRow),
  };

  const resolvedProps = new Set(['descriptor', 'length', 'locationCode']);
  const optionalCols: ColumnRegular[] = [];
  for (const f of SELLER_FIELDS) {
    if (resolvedProps.has(f.key)) continue; // shown via the Product/Length/Location pickers
    const base: ColumnRegular = {
      prop: f.key,
      name: t(`fields.${f.key}`),
      size: widths[f.key] ?? 150,
      sortable: true,
      filter: true,
      cellProperties: highlight(f.key, tRef),
    };
    if (f.options) {
      optionalCols.push({
        ...base,
        cellTemplate: Template(EnumCell, {
          handlers: handlersRef,
          field: f.key,
          options: f.options,
        }),
      });
    } else if (f.type === 'number') {
      optionalCols.push({ ...base, editor: 'number' });
    } else {
      optionalCols.push(base);
    }
  }

  const statusCol: ColumnRegular = {
    prop: '__status',
    name: t('review.match_column'),
    size: 110,
    readonly: true,
    sortable: true,
  };
  return [
    actionsCol,
    productCol,
    lengthCol,
    locationCol,
    ...optionalCols,
    statusCol,
  ];
}

/** Re-resolve a row after a paste/inline edit lands raw text in a resolved-picker column. */
export function ReviewStep({
  file,
  mapping,
  onBack,
  backLabel,
}: ReviewStepProps) {
  const { t } = useTranslation('inventory-import');
  // Stable handlers ref; the grid columns are built once and cell templates call the latest
  // delete/pick/set handlers at click time.
  const handlersRef = useRef<Handlers>({
    onDelete: () => {},
    onPick: () => {},
    onSet: () => {},
  });
  // The React wrapper doesn't type the copy event, so attach it on the element directly to rewrite
  // copied product ids / location codes into their display values.
  const gridRef = useRef<HTMLRevoGridElement>(null);
  // Prop order of the non-pinned (center) columns, so a copied cell's column index maps to a prop and
  // the rewrite stays scoped to Product/Location. Kept in a ref because the copy listener is bound
  // once; updated whenever the columns change (see below).
  const centralPropsRef = useRef<string[]>([]);
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const onCopy = (e: Event) => {
      const detail = (
        e as CustomEvent<{ data?: unknown[][]; range?: { x?: number } }>
      ).detail;
      if (detail?.data)
        rewriteCopiedData(
          detail.data,
          centralPropsRef.current,
          detail.range?.x ?? 0
        );
    };
    el.addEventListener('clipboardrangecopy', onCopy);
    return () => el.removeEventListener('clipboardrangecopy', onCopy);
  }, []);
  // Stable t ref so the columns (built once) and the per-cell tooltip highlight always read the
  // latest translation function without rebuilding the column definitions.
  const tRef = useRef<TFunction>(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const editors = useMemo(() => ({ number: Editor(NumberEditor) }), []);

  const initial = useMemo(() => {
    const rows = buildRows(file, mapping);
    const res = validateRows(rows);
    return { rows, res };
  }, [file, mapping]);

  // Fit each content column to the imported data (the file is stable for the step). Built once with
  // the columns so editing/adding rows doesn't reset grid state; the user can still drag-resize.
  const widths = useMemo(
    () => computeColumnWidths(initial.rows, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fit to the imported data once; t is read via tRef in the columns
    [initial.rows]
  );
  const columns = useMemo(
    () => buildColumns(handlersRef, tRef, widths),
    [widths]
  );
  useEffect(() => {
    centralPropsRef.current = columns.flatMap((c) =>
      c.pin !== 'colPinStart' ? [String(c.prop)] : []
    );
  }, [columns]);

  const review = useReviewRows(initial);
  const {
    rows,
    validation,
    filters,
    setFilters,
    serverErrors,
    setServerErrors,
    visibleRows,
  } = review;

  const navigate = useNavigate();
  const { mutate: importInventory, isPending: isImporting } =
    useImportSellerInventory();

  useEffect(() => {
    handlersRef.current.onDelete = review.deleteRow;
    handlersRef.current.onPick = review.pickProduct;
    handlersRef.current.onSet = review.setField;
  }, [review.deleteRow, review.pickProduct, review.setField]);

  // Per-row heights so cells can wrap to multiple lines (indexed to the current row order).
  const rowDefinitions = useMemo<RowDefinition[]>(
    () =>
      visibleRows.map((row, index) => ({
        type: 'rgRow',
        index,
        size: estimateRowHeight(row, widths),
      })),
    [visibleRows, widths]
  );

  // Grow the grid to fit its content (no inner scroll) instead of a fixed height — header plus the
  // summed row heights — capped so a very large import still virtualizes within an inner scroll.
  const gridHeight = useMemo(() => {
    const rowsPx = rowDefinitions.reduce((sum, d) => sum + d.size, 0);
    return Math.min(GRID_MAX_PX, GRID_HEADER_PX + rowsPx + 2);
  }, [rowDefinitions]);

  const { autoMatched, needsPick } = useMemo(() => {
    let auto = 0;
    let unresolved = 0;
    for (const r of rows) {
      if (r.__status === 'auto') auto++;
      if (!r.product) unresolved++;
    }
    return { autoMatched: auto, needsPick: unresolved };
  }, [rows]);

  // The exact rows that will be sent (no error-level issues; warnings are fine). Drives the button
  // label/enablement and the import call, so they can't drift apart.
  const payload = useMemo(() => toImportRows(rows), [rows]);
  const importable = payload.length;
  const blocked = rows.length - importable;

  const runImport = () => {
    if (payload.length === 0 || isImporting) return;
    setServerErrors([]);
    importInventory(
      { rows: payload },
      {
        onSuccess: (data) => {
          const result = data.importSellerInventory;
          if (result.failed.length === 0) {
            toast.success(
              t('review.import_success', { count: result.imported })
            );
            navigate('/my-products');
            return;
          }
          // All-or-nothing: a rejected row rolls back the whole import (nothing persisted). Keep the
          // user here with the rejected rows + reasons so they can fix and re-import.
          toast.error(
            t('review.import_rejected', { count: result.failed.length })
          );
          setServerErrors(result.failed);
        },
        onError: () => toast.error(t('review.import_error')),
      }
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <SummaryBar
        rowCount={rows.length}
        autoMatched={autoMatched}
        needsPick={needsPick}
        errors={validation.errors}
        warnings={validation.warnings}
      />

      <ReviewToolbar
        search={filters.search}
        onSearchChange={(value) =>
          setFilters((f) => ({ ...f, search: value, focusRowId: null }))
        }
        onlyIssues={filters.onlyIssues}
        onToggleOnlyIssues={(value) =>
          setFilters((f) => ({ ...f, onlyIssues: value, focusRowId: null }))
        }
        visibleCount={visibleRows.length}
        totalCount={rows.length}
        onAddRow={review.addRow}
        atRowCap={rows.length >= MAX_ROWS}
      />

      <p className="text-xs text-neut-sec">{t('review.instructions')}</p>

      {filters.focusRowId != null && (
        <FocusBanner
          rowId={filters.focusRowId}
          onShowAll={() => setFilters((f) => ({ ...f, focusRowId: null }))}
        />
      )}

      <div
        className="inventory-import-grid overflow-hidden rounded-lg border border-neut-border shadow-sm"
        style={{ height: gridHeight }}
      >
        <RevoGrid
          columns={columns}
          source={visibleRows}
          editors={editors}
          range
          resize
          rowHeaders
          filter
          theme="compact"
          // Base row height (compact default is ~32px); per-row heights below grow rows to fit
          // wrapped cell content.
          ref={gridRef}
          rowSize={44}
          rowDefinitions={rowDefinitions}
          // Custom elements default to display:inline, which collapses height to 0; force a block.
          style={{ display: 'block', height: '100%', width: '100%' }}
          onAfteredit={review.handleAfterEdit}
        />
      </div>

      <IssuesPanel
        reports={validation.reports}
        onJump={(rowId) =>
          setFilters((f) => ({ ...f, search: '', focusRowId: rowId }))
        }
      />

      <ImportActions
        onBack={onBack}
        backLabel={backLabel}
        importable={importable}
        blocked={blocked}
        isImporting={isImporting}
        onImport={runImport}
      />

      <ServerErrorList
        errors={serverErrors}
        onJump={(rowId) =>
          setFilters((f) => ({
            ...f,
            search: '',
            onlyIssues: false,
            focusRowId: rowId,
          }))
        }
      />
    </div>
  );
}
